import React, { useState, useCallback, useMemo } from "react";
import { type Asset } from "@/services/api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrashAlt,
  faFilters,
  faLock,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import AssetSearchCombobox from "./AssetSearchCombobox";
import {
  formatAssetDisplayName,
  createAssetMappings,
} from "@/utils/playground";
import clsx from "clsx";
import { Tooltip } from "@/components/ui/tooltip/Tooltip";
import { ColorPicker } from "@/components/common/ColorPicker";
import { Button } from "@/components/ui/button/Button";
import { useCompanyColors } from "@/hooks/useCompanyColors";
import { getChartSeriesColors } from "@/components/chart/styles/colors";

// Define a safer method to get resource ID that handles the TypeScript issues
function getAssetResourceId(asset: Asset): string | undefined {
  // Type assertion to any to work around the type mismatch
  const assetAny = asset as any;

  // Check multiple possible locations for resource_id
  if (assetAny?.metadata?.resource_id) {
    return assetAny.metadata.resource_id;
  }

  // Check if resource_id is directly on the asset
  if (assetAny?.resource_id) {
    return assetAny.resource_id;
  }

  // Check if id is available
  if (assetAny?.id) {
    return assetAny.id;
  }

  return undefined;
}

export interface DataSettingsProps {
  selectedAssets: string[];
  toggleAsset: (asset: string, displayName?: string) => void;
  assetColors: Record<string, string>;
  setAssetColor: (assetId: string, color: string) => void;
  assetDisplayNames?: Record<string, string>;
  isSearchOpen: boolean;
  setIsSearchOpen: (isOpen: boolean) => void;
  showColorPickers?: boolean;
  includeWatchlists?: boolean;
  selectedWatchlistId?: string;
  onSelectWatchlist?: (watchlistId: string) => void;
  hideAddButton?: boolean;
  onSendToScreener?: () => void;
  /** Resource IDs for which the API returned an access-denied (403) response. */
  accessDeniedIds?: Set<string>;
}

const DataSettings: React.FC<DataSettingsProps> = ({
  selectedAssets,
  toggleAsset,
  assetColors,
  setAssetColor,
  assetDisplayNames = {},
  isSearchOpen,
  setIsSearchOpen,
  showColorPickers = false,
  includeWatchlists = false,
  selectedWatchlistId,
  onSelectWatchlist,
  hideAddButton = false,
  onSendToScreener,
  accessDeniedIds,
}) => {
  const [searchResults, setSearchResults] = useState<Asset[]>([]);

  // Get company colors for fallback palette (same pattern as chart adapters)
  const { chartColors: companyChartColors } = useCompanyColors();
  const colorPalette = useMemo(
    () => getChartSeriesColors(companyChartColors),
    [companyChartColors]
  );

  const handleOpenSearch = useCallback(() => {
    setIsSearchOpen(true);
  }, [setIsSearchOpen]);

  // Create consistent asset mappings for display names
  const assetMappings = useMemo(() => {
    return createAssetMappings(selectedAssets);
  }, [selectedAssets]);

  // Get display name with priority: assetDisplayNames > asset.display_name > assetMappings > formatted asset ID
  const getAssetDisplayName = (assetId: string, asset?: Asset): string => {
    // First check if we have a display name in the passed props
    if (assetDisplayNames && assetDisplayNames[assetId]) {
      return assetDisplayNames[assetId];
    }

    // Then check if we have the asset object with a display_name
    if (asset?.display_name) {
      return asset.display_name;
    }

    // Then check our asset mappings
    if (assetMappings.displayNames[assetId]) {
      return assetMappings.displayNames[assetId];
    }

    // Finally fall back to formatting the asset ID
    return formatAssetDisplayName(assetId);
  };

  // Handle selecting an asset from the search dialog
  const handleSelectAsset = useCallback(
    async (asset: Asset) => {
      const resourceId = getAssetResourceId(asset);

      // Validate the asset has a proper resource ID
      if (!asset || !resourceId) {
        return;
      }

      // Extract resource type and stream type from the asset
      const resourceType = asset.resource_type || "product";
      const streamType = (asset as any).default_stream_type || "default";

      // Create a custom display name that includes metadata
      const displayName = asset.display_name;
      const fullDisplayName = `${displayName}:::${resourceId}:::${resourceType}:::${streamType}`;

      // If the asset isn't in our local state, add it
      if (!searchResults.some((a) => getAssetResourceId(a) === resourceId)) {
        setSearchResults((prev) => [...prev, asset]);
      }

      // Pass the full display name with metadata when toggling the asset
      toggleAsset(resourceId, fullDisplayName);

      // Close the dialog
      setIsSearchOpen(false);
    },
    [toggleAsset, searchResults, setIsSearchOpen]
  );

  // Handle removing an asset
  const handleRemoveAsset = useCallback(
    (assetId: string | undefined | null) => {
      // Validate the asset ID
      if (!assetId) {
        return;
      }

      // Toggle the asset (which will remove it from selectedAssets and update URL)
      toggleAsset(assetId);
    },
    [toggleAsset]
  );

  return (
    <div className="">
      <div className="space-y-4 h-full">
        {/* Selected Assets */}
        <div className="h-full">
          <div className="h-full">
            {selectedAssets.map((assetId, index) => {
              // Find the asset in search results if available
              const asset = searchResults.find(
                (a) => getAssetResourceId(a) === assetId
              );

              // Get the display name using consistent approach
              const displayName = getAssetDisplayName(assetId, asset);

              // Get the consistent color for this asset with fallback (same pattern as chart adapters)
              const assetColor =
                assetColors[assetId] ||
                colorPalette[index % colorPalette.length];

              const isDenied = accessDeniedIds?.has(assetId) ?? false;

              const row = (
                <div
                  className={clsx(
                    "flex justify-between group/resource -ml-2 items-center rounded",
                    isDenied && "opacity-50"
                  )}
                >
                  <div className="flex w-full items-center truncate py-1.5 px-2 gap-3 hover:bg-slate-100 rounded-lg dark:hover:bg-slate-700">
                    {showColorPickers && !isDenied ? (
                      <ColorPicker
                        currentColor={assetColor}
                        onSelectColor={(color) => setAssetColor(assetId, color)}
                      />
                    ) : (
                      <div
                        className="h-2 w-2 flex-none rounded-full"
                        style={{
                          backgroundColor: isDenied ? "transparent" : (assetColor || "transparent"),
                        }}
                      />
                    )}
                    <div className="truncate flex-grow text-slate-900 dark:text-white">
                      {displayName}
                    </div>
                    {isDenied ? (
                      <FontAwesomeIcon
                        icon={faLock}
                        className="text-slate-400 dark:text-slate-500 flex-none text-xs"
                      />
                    ) : (
                      <button
                        onClick={() => handleRemoveAsset(assetId)}
                        className="cursor-pointer text-slate-500 hidden group-hover/resource:flex hover:text-slate-800 dark:text-slate-50 dark:hover:text-white"
                        title="Remove asset"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    )}
                  </div>
                </div>
              );

              if (isDenied) {
                return (
                  <Tooltip
                    key={assetId}
                    content="You don't have access to this resource"
                    placement="left"
                    delay={50}
                  >
                    {row}
                  </Tooltip>
                );
              }

              return <React.Fragment key={assetId}>{row}</React.Fragment>;
            })}
            {!hideAddButton && (
              <Button
                color="indigo"
                className="w-full mt-4"
                onClick={handleOpenSearch}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Resource
              </Button>
             
            )}
            {onSendToScreener && selectedAssets.length > 0 && (
              <Button
                color="color"
                className="w-full mt-4"
                onClick={onSendToScreener}
              >
                <FontAwesomeIcon icon={faFilters} /> Send to Screener
              </Button>
             
            )}
          </div>
        </div>
      </div>

      {/* Asset Search Dialog */}
      <AssetSearchCombobox
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSelectAsset={handleSelectAsset}
        selectedAssets={selectedAssets}
        includeWatchlists={includeWatchlists}
        selectedWatchlistId={selectedWatchlistId}
        onSelectWatchlist={(wl) => {
          onSelectWatchlist?.(wl.id);
        }}
      />
    </div>
  );
};

export default DataSettings;
